import type { Role } from '@time-tracking/shared';
import type { ReactNode } from 'react';

import { useMe } from '../api/hooks.js';
import { Layout } from '../components/Layout.js';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { Doors } from './help/Doors.js';
import { FaqAccordion, resolveFaqSection } from './help/FaqAccordion.js';
import { PageFooterRows } from './help/PageFooterRows.js';
import { SectionHeader } from './help/SectionHeader.js';
import './help-faq.css';

/**
 * Right-hand note on the Quick answers section header, per role.
 *
 * Keyed by `Role` rather than assembled from fragments: the prototype computes the article
 * from the role word's first letter, which is a rule with exactly two data points and no
 * third case that needs it. A map keyed by the union says the same thing, fails to compile
 * when a role is added, and keeps each string readable as the sentence it is.
 */
const FAQ_SCOPE_NOTE: Readonly<Record<Role, ReactNode>> = {
  contributor: (
    <>
      Written for <b>contributors</b>
    </>
  ),
  approver: (
    <>
      Everything an <b>approver</b> can do
    </>
  ),
  administrator: (
    <>
      Everything an <b>administrator</b> can do
    </>
  ),
};

/**
 * Help & FAQ — one page serving four intents, in descending order of frequency: find an
 * answer, report a data problem, report an app defect, share an idea.
 *
 * PROTOTYPE OF RECORD: `design_handoff_help_faq/`. This component owns the composition —
 * the five stacked sections in the prototype's order, the content column, and the page
 * header — while each section's body is its own component under `./help/`.
 *
 * It also owns the page's single `useMe` call. Role drives the FAQ tiers (U6), Door A's
 * guidance and Door B's prefill (U8), so it is resolved once here and passed down. Three
 * sections each calling `useMe` would work, but they would then each own a loading state
 * and could disagree about the role mid-flight.
 */
export function HelpPage() {
  usePageTitle('Help & FAQ');

  const meQuery = useMe();
  const role = meQuery.data?.role;

  // Resolved once, here, because two things downstream depend on the same verdict: the
  // accordion body and the section note above it, which is suppressed unless the role is
  // known AND something is published. Computing it in both places is how a note reading
  // "Written for contributors" ends up sitting over an empty-state callout.
  const faqSection = resolveFaqSection({ role, roleUnavailable: meQuery.isError });

  return (
    // No `pageTitle` on purpose: the brand lockup stays "Timesheets / Campus Solutions",
    // which is what the prototype's top bar shows on this page.
    <Layout>
      <div className="help-page">
        {/* 1 · Page header */}
        <div className="help-head">
          <h1>Help &amp; FAQ</h1>
          <p>Answers to common questions, and where to go when something looks wrong.</p>
        </div>

        {/* 2 · Quick answers */}
        <section className="help-sec" aria-labelledby="help-quick-answers">
          {/* The note renders for exactly one state — role known, answers published. It
              stays absent while `useMe` is pending rather than defaulting to contributor
              (a default would show an administrator the wrong scope for a frame or two,
              and this note's whole job is to say whose answers these are); the empty state
              suppresses it because there is no scope to describe; and the degraded state
              suppresses it because the role is unknown there, which is the same reason the
              pending state does. In that last case the callout inside the accordion says
              so in words instead. */}
          <SectionHeader
            id="help-quick-answers"
            title="Quick answers"
            note={faqSection.kind === 'answers' ? FAQ_SCOPE_NOTE[faqSection.role] : null}
          />
          <FaqAccordion state={faqSection} />
        </section>

        {/* 3 · Something looks wrong */}
        <section className="help-sec" aria-labelledby="help-something-wrong">
          <SectionHeader id="help-something-wrong" title="Something looks wrong" />
          {/* `role` is passed straight through, `undefined` and all: Door B omits the role
              field from its prefill rather than defaulting it while /v1/me is pending or
              failed. Door A takes no role at all — its guidance is the same for everyone,
              see Doors.tsx. */}
          <Doors role={role} />
        </section>

        {/* 4 · Ideas and requests, and 5 · email fallback + build info — the three
            subordinate rows, in one component because being quieter than the sections above
            them is the decision they share. No `role` prop: none of the three varies by
            role, which is half of what R3 asks (role changes which answers are shown, never
            which reporting paths exist). They are not wrapped in a `section.help-sec`
            either — that class carries the 20px section header these rows deliberately do
            not get. */}
        <PageFooterRows />
      </div>
    </Layout>
  );
}
